/* eslint-disable @stylistic/max-len */
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { BASE_ROUTES, HOUR_IN_SECONDS, SECOND } from 'core/common/constants';
import { Config } from 'core/common/contexts';
import { AppEnvironment } from 'core/common/entities';
import { PaymentOrderStatus } from 'core/payments/entities';
import { ScanRatio } from './flow/entities';

export const config: Config = {
  appVersion: process.env.NEXT_PUBLIC_CI_APP_VERSION || 'fallback-release',
  appEnvironment: process.env.NEXT_PUBLIC_APP_ENV as AppEnvironment,
  address: 'Angelou Sikelianou 3, Apartment no. 304, Petrou kai Pavlou, 3085 Limassol, Cyprus',
  geocodeUrl: process.env.NEXT_PUBLIC_GEOCODE_URL ?? '',
  geocodeApiKey: 'ge-38779bc84df71d40',
  apiPrefix: 'v1',
  applePayContainerId: 'apple-pay-button-container',
  googlePayContainerId: 'google-pay-button-container',
  pixAutomaticoContainerId: 'pix-automatico-container',
  pixQrContainerId: 'pix-qr-container',
  supportCenter: 'https://osupporto-help.zendesk.com/hc/en-us',
  supportEmail: 'support@nebulahoroscope.com',
  legalSupportEmail: 'support@asknebula.com',
  projectName: 'Nebula',
  contactEmails: {
    general: 'support@asknebula.com',
    pr: 'pr@asknebula.com',
    hiring: 'hiring@asknebula.com',
  },
  manageSubscriptionUrl: 'https://m.asknebula.com/app/settings/subscriptions',
  dateOfMoneyBack: '3 November 2025',
  appName: process.env.NEXT_PUBLIC_APP_NAME ?? '',
  rootDomain: process.env.NEXT_PUBLIC_ROOT_DOMAIN ?? '',
  appleSupport: 'https://support.apple.com/en-us/HT202039',
  googleSupport:
    'https://support.google.com/googleplay/answer/7018481?co=GENIE.Platform%3DAndroid&hl=en',
  testingTokenCookieKey: 'tester_token',
  apiUrl: process.env.NEXT_PUBLIC_API_URL ?? '',
  mainAppUrl: process.env.NEXT_PUBLIC_MAIN_APP_URL ?? '',
  domainUrl: process.env.NEXT_PUBLIC_DOMAIN_URL ?? '',
  funnelPlatform: 'AppNebula',
  analytics: {
    amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '',
    amplitudeApiEndpoint: process.env.NEXT_PUBLIC_AMPLITUDE_API_ENDPOINT ?? '',
  },
  expensivePriceTreshold: 6,
  askNebulaUrl: process.env.NEXT_PUBLIC_ASK_NEBULA_URL ?? '',
  scanRatio: {
    [ScanRatio.PORTRAIT]: {
      width: 248,
      height: 330,
    },
    [ScanRatio.SQUARE]: {
      width: 300,
      height: 300,
    },
  },
  palmAnimationDuration: 3 * SECOND,
  isProduction: process.env.NEXT_PUBLIC_APP_ENV === 'production',
  isStage: process.env.NEXT_PUBLIC_APP_ENV === 'stage',
  isDevelopment: process.env.NODE_ENV === 'development',
  isBuildPhase: process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD,
  sentryDsn: process.env.NEXT_PUBLIC_SENTRY_DSN ?? '',
  addressUS: '500 North Rainbow Blvd, Suite300, Las Vegas, Nevada 89107',
  addressCN: '14/F, China Hong Kong Tower, 8 Hennessy Road, Wan Chai, Hong Kong',
  cdn: 'https://media.asknebula.com',
  oneTrust: {
    key: process.env.NEXT_PUBLIC_ONE_TRUST_KEY ?? '',
  },
  growthbook: {
    apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_URL ?? '',
    clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY ?? '',
    decryptionKey: process.env.NEXT_PUBLIC_GROWTHBOOK_DECRYPTION_KEY ?? '',
  },
  grafanaLogger: {
    url: process.env.NEXT_PUBLIC_GRAFANA_URL || '',
  },
  funnelBuilder: {
    apiHost: process.env.NEXT_PUBLIC_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_FUNNEL_BUILDER_API_KEY ?? '',
    requestTimeout: 30 * SECOND,
    maxRequestRetries: 2,
    requestRetryDelay: SECOND,
    isLanguageScoped: false,
  },
  SVGSprite: {
    inputPath: 'svg-sprites/assets/icons',
    outputPath: 'common/assets/images/sprites',
    typesOutputPath: 'svg-sprites/entities',
    outputName: 'sprite.svg',
  },
  observability: {
    defaultTracerName: 'default_tracer',
    tracesUrl: process.env.NEXT_PUBLIC_TRACES_URL ?? '',
    browser: {
      httpInstrumentationIgnoreUrls: [process.env.NEXT_PUBLIC_GRAFANA_URL].filter(
        Boolean,
      ) as Array<string>,
      httpInstrumentationSpanRate: 0.2,
    },
    server: {
      httpInstrumentationIgnoreOutGoingUrls: [],
      httpInstrumentationIgnoreIncomingUrls: [
        /api\/____service\/healthcheck/,
        /next\/image/,
        /next\/static/,
        /next\/data/,
        /images/,
        /favicon/,
      ],
      httpInstrumentationSpanRate: 0.2,
    },
  },
  errorMonitoring: {
    enabled:
      process.env.NEXT_PUBLIC_APP_ENV === 'production' &&
      process.env.NEXT_PUBLIC_CI_ERROR_MONITORING_DISABLED !== 'true',
    // see more: https://www.notion.so/Sentry-ingnore-list-appnebula-2a8bd0e8363880bfa924f36256ae709e?source=copy_link
    ignoreErrors: [
      /Minified\s+React\s+error\s+#(418|423|425)/gi,
      /Failed to export with XHR\s*\(status:\s*0\)/i,
      /OTLPExporterError/i,
      /undefined is not an object \(evaluating 'r\.DomainData'\)/i,
      /undefined is not an object \(evaluating 't\.Domain'\)/i,
      /Cannot read properties of undefined \(reading 'Domain'\)/i,
      /null is not an object \(evaluating 'this\.selector\.appendChild'\)/i,
      /Cannot read properties of null \(reading 'appendChild'\)/i,
      /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers'\)/i,
      /Java object is gone/i,
      /zoid destroyed all components/i,
      /Document is ready and element #paypal-button does not exist/i,
      /Container for payment form with id: solid-payment-form-container does not exist/i,
      /Detected container element removed from DOM/i,
      /Can't find variable: CONFIG/i,
      /Failed to execute 'dispatchEvent' on 'EventTarget': Function statements require a function name/i,
    ],
    ignoreTraces: [],
  },
  buildCache: {
    path: '/.build-request-cache',
  },
  ebanxUrl: 'https://www.ebanx.com/pt-br/legal',
  clarityId: process.env.NEXT_PUBLIC_CLARITY_ID ?? '',
  verificationIds: {
    trustpilot: 'ea688f7d-9420-4940-b046-f90ff48348a7',
    facebook: 'ld6fauq8wysgqi8nxb014927mbq37x',
  },
  supportForm: {
    id: 1,
    descriptionMaxLength: 4000,
    maxAttachments: 5,
    maxAttachmentSizeBytes: 10 * 1024 * 1024,
    allowedMimeTypes: ['image/svg+xml', 'image/png', 'image/jpeg', 'image/gif'],
  },
  funnelBuilderTrustedOrigins: [
    'http://localhost:3333',
    'https://funnel-builder.obrio.net',
    'https://gateway.appnebula.co',
    'https://stage-funnel-builder.obrio.net',
  ],
  companyName: 'Spiritual Nebula Limited',
  companyNameUS: 'Yolo Brothers Inc',
  companyNameCN: 'Beku Limited',
  supportPhoneNumber: '+1 (504) 738-8774',
  recurringPayment: {
    refetchDelay: 3 * SECOND,
    maxRetryAttempts: 5,
  },
  web2appLink: 'https://nebulaapp.onelink.me/AaEA/751hl9nq',
  emailConsentRequestDelay: 5 * SECOND,
  paymentTrustedOrigin: 'https://form.solidgate.com',
  funnelsWithMinimalAnalytics: ['hypno-healing-thinking', 'hypno-healing'],
  funnelsWithoutClarity: ['hypno-healing'],
  maxNameLength: 30,
  scrollTrackingThresholdsPercents: [5, 10, 20, 40, 60, 80, 100],
  activePaymentStatuses: [
    PaymentOrderStatus.AUTH_OK,
    PaymentOrderStatus.APPROVED,
    PaymentOrderStatus.SETTLE_OK,
    PaymentOrderStatus.PROCESSING,
    PaymentOrderStatus.VOID_OK,
    PaymentOrderStatus.PARTIAL_SETTLED,
  ],
  marketing: {
    fallbackEltv: 30,
  },
  techLinks: {
    terms: BASE_ROUTES.TECH_TERMS,
    privacyPolicy: BASE_ROUTES.PRIVACY_POLICY,
    fullDisclaimer: BASE_ROUTES.TECH_FULL_DISCLAIMER,
    impressum: BASE_ROUTES.TECH_IMPRESSUM,
    legalCenter: BASE_ROUTES.TECH_LEGAL_CENTER,
    faq: BASE_ROUTES.TECH_FAQ,
    moneyBackPolicy: BASE_ROUTES.TECH_MONEY_BACK_POLICY,
    cookiePolicy: BASE_ROUTES.TECH_COOKIE_POLICY,
  },
  legalDisclosure: {
    companyName: 'Spiritual Nebula Limited',
    address: 'Angelou Sikelianou 3, Apartment no. 304, Petrou kai Pavlou, 3085 Limassol, Cyprus',
    jurisdiction: 'the Republic of Cyprus',
    registrationNumber: 'HE 465326',
    vatNumber: '60056798U',
  },
  redis: {
    master: {
      url: process.env.REDIS_URL ?? '',
      staleAge: HOUR_IN_SECONDS,
    },
    replicas: {
      url: process.env.REDIS_REPLICAS_URL ?? '',
      staleAge: HOUR_IN_SECONDS,
    },
    funnelBuilder: {
      url: process.env.REDIS_FUNNEL_BUILDER_URL ?? '',
      staleAge: HOUR_IN_SECONDS,
      commandTimeoutMs: 2 * SECOND,
      disableOfflineQueue: true,
    },
  },
  revalidation: {
    pathsPerRequest: 10,
  },
  funnelsCdn: {
    pipelineUrl: process.env.FUNNELS_CDN_PIPELINE_URL ?? '',
    service: process.env.SERVICE_VARIANT ?? '',
    token: process.env.FUNNELS_CDN_TOKEN ?? '',
  },
  partnersLinks: {
    glamour: 'https://www.glamourmagazine.co.uk/article/zodiac-sign-personality-traits',
    marieClaire:
      'https://www.marie-claire.es/horoscopo/signos-zodiaco-afectados-luna-llena-flores-mayo.html',
    cosmopolitan: 'https://www.cosmopolitan.com/lifestyle/g29762175/best-horoscope-apps/',
    vogue: 'https://www.vogue.com/article/twin-flames',
    forbes:
      'https://www.forbes.com/sites/rebeccahughes/2024/10/27/perfect-winter-destinations-in-europe-according-to-your-zodiac-sign/',
    newYorkTimes: 'https://www.nytimes.com/2022/05/23/style/astrology-dating-app-ilios.html',
    newYorkPost:
      'https://nypost.com/astrology/the-best-and-worst-times-to-get-married-according-to-astrologers/',
    grazia:
      'https://graziamagazine.com/us/articles/zodiac-signs-weatlth-destined-for-billionaire-status/',
    bustle: 'https://www.bustle.com/life/zodiac-signs-friday-the-13th',
    huffpost:
      'https://www.huffingtonpost.co.uk/entry/dreaming-about-school-exams-adult_uk_67b70442e4b050f805a12e00',
    popsugar: 'https://www.popsugar.com/love/what-is-a-twin-flame-46824264',
  },
  forceEnableLRU: process.env.FORCE_ENABLE_LRU ?? 'false',
  legalFlows: ['default_flow_uuu_search_pmax', 'default_flow_uuu'],
};
